import { Controller } from 'stimulus';
import { get, post, put, FetchRequest } from '@rails/request.js';

export default class extends Controller {
  static values = {
    availableCredits: Number,
    creditPrice: Number,
    minimumCredits: Number,
    imageReady: Boolean,
    cardUrl: String,
  };

  static targets = [
    'creditsAmount',
    'creditsAmountInput',
    'creditsCallout',
    'creditsRemaining',
    'creditsUsdAmount',
    'enhanceButton',
    'enhancedImageId',
    'flash',
    'hint',
    'enhancementForm',
    'originalImage',
    'resultImage',
    'submitButton',
  ];

  async submitForm(event) {
    event.preventDefault();

    this.#showSection('enhanced-images__loading');
    this.dispatch('busy');

    const body = new FormData(this.enhancementFormTarget);
    const url = this.enhancementFormTarget.action;
    // A dropped connection rejects instead of answering with an error status.
    const response = await post(url, { body }).catch(() => null);
    this.dispatch('idle');

    if (response?.ok) {
      this.#hideFlash();

      const result = await response.json;

      this.enhancedImageIdTarget.value = result.id;

      const enhancedImageUrl = result.url;
      const newImage = this.resultImageTarget;
      newImage.dataset.enhanced = enhancedImageUrl;

      this.#updateRemainingCreditsCallout(result.available_credits);
      this.dispatch('rendered', { target: document });

      // smoothen transition: wait for image to fully load before showing result section
      newImage.onload = () => {
        this.#showSection('enhanced-images__result');
        newImage.onload = null; // Clean up
      };
      newImage.src = enhancedImageUrl;
    } else {
      this.#showSection('enhanced-images__form');
      this.#showFlash(
        'Something went wrong while generating your render. Please try again.',
        'error'
      );
    }
  }

  // The board_show_v2 sidebar card and the legacy pages' callout listen for `enhanced-images:rendered` and swap in
  // their re-rendered selves, so the new render shows up in their History.
  async refreshCard() {
    if (!this.hasCardUrlValue) {
      return;
    }

    await get(this.cardUrlValue, { responseKind: 'turbo-stream' });
  }

  toggleSubmit() {
    if (!this.imageReadyValue) {
      return;
    }

    // The prompt is optional: the default photorealistic prompt is always sent.
    this.submitButtonTarget.disabled = this.availableCreditsValue < this.minimumCreditsValue;
  }

  toggleFeedbackSubmit() {
    const checkedOutput = this.element.querySelector('input[name="feedback_output"]:checked');
    const checkedFuture = this.element.querySelector('input[name="feedback_future"]:checked');
    const filledFeedback =
      this.element.querySelector('textarea[name="feedback_text"]').value.trim().length > 0;

    this.element.querySelector('.p-button--enhanced-images__share-feedback').disabled =
      !checkedOutput || !checkedFuture || !filledFeedback;
  }

  toggleImage(event) {
    this.resultImageTarget.src = this.resultImageTarget.dataset[event.target.dataset.imageType];
  }

  showPayment(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__payment');
  }

  retryPrompt(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__form');
  }

  openFeedbackSection(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__feedback');
  }

  openResultSection(event) {
    event.preventDefault();
    this.#showSection('enhanced-images__result');
  }

  allow(event) {
    // The modal binds this action without the param: it has no enhance button to enable.
    const removeClasses = event.params.removeClasses?.split(' ') ?? [];
    if (this.hasEnhanceButtonTarget) {
      this.enhanceButtonTarget.classList.remove(...removeClasses);
      this.enhanceButtonTarget.removeAttribute('inert');
    }

    this.imageReadyValue = true;

    if (this.hasSubmitButtonTarget) {
      this.toggleSubmit();
    }

    if (this.hasResultImageTarget) {
      this.resultImageTarget.dataset.original = event.detail.data.variants['sharpen'];
    }
  }

  async submitFeedback() {
    const feedbackOutput = this.element.querySelector(
      'input[name="feedback_output"]:checked'
    )?.value;
    const feedbackFuture = this.element.querySelector(
      'input[name="feedback_future"]:checked'
    )?.value;
    const feedbackText = this.element.querySelector('textarea[name="feedback_text"]').value;

    const enhancedImageId = this.enhancedImageIdTarget.value;
    const body = JSON.stringify({
      enhanced_image: {
        feedback_output: feedbackOutput,
        feedback_future: feedbackFuture,
        feedback_text: feedbackText,
      },
    });

    const response = await put(`/api/df/enhanced_images/${enhancedImageId}`, { body });

    if (response.ok) {
      this.#showSection('enhanced-images__feedback-completed');
    }
  }

  updatePurchaseCredits(event) {
    this.creditsAmountTarget.textContent = parseInt(event.currentTarget.value) || 0;
    this.creditsUsdAmountTargets.forEach((total) => {
      total.textContent = (event.currentTarget.value * this.creditPriceValue).toFixed(2);
    });
  }

  async purchaseCredits() {
    const creditPurchaseSection = this.element.querySelector('.enhanced-images__payment');
    const purchasedCredits = this.creditsAmountInputTarget.value;
    creditPurchaseSection.classList.add('enhanced-images__payment--loading');
    this.dispatch('busy');

    const body = JSON.stringify({
      amount: purchasedCredits,
    });

    const request = new FetchRequest('post', '/credit_purchases', { body });
    const response = await request.perform().catch(() => null);

    if (response?.ok) {
      const result = await response.json;
      this.availableCreditsValue = result.available_credits;
      this.#showSection('enhanced-images__form');
      this.#updateRemainingCreditsCallout(result.available_credits);
      this.#resetCreditPurchaseForm();
      this.#showFlash(`${purchasedCredits} AI credits purchased successfully.`, 'success');
    } else {
      this.#showFlash(
        'We encountered an unexpected error. Please try your purchase again.',
        'error'
      );
    }
    this.toggleSubmit();
    creditPurchaseSection.classList.remove('enhanced-images__payment--loading');
    this.dispatch('idle');
  }

  cancelPurchase(event) {
    event.preventDefault();
    this.#resetCreditPurchaseForm();
    this.#showSection('enhanced-images__form');
  }

  downloadImage(event) {
    event.preventDefault();

    const enhancedImageId = this.enhancedImageIdTarget.value;
    const imageUrl = `/api/df/enhanced_images/${enhancedImageId}`;

    window.open(imageUrl, '_blank');
  }

  toggleHistory(event) {
    event.preventDefault();
    this.#history.classList.toggle('enhanced-images-callout__history--active');
  }

  closeHistory(event) {
    if (event.type === 'click' && this.element.contains(event.target)) {
      return;
    }

    // The sidebar card renders the History panel only once the design has a render.
    this.#history?.classList.remove('enhanced-images-callout__history--active');
  }

  get #history() {
    return this.element.querySelector('.enhanced-images-callout__history');
  }

  #resetCreditPurchaseForm() {
    this.creditsAmountInputTarget.value = '';
    this.creditsAmountTarget.textContent = 0;
    this.creditsUsdAmountTargets.forEach((total) => {
      total.textContent = 0;
    });
  }

  #updateRemainingCreditsCallout(availableCredits) {
    this.creditsRemainingTargets.forEach((creditsRemaining) => {
      creditsRemaining.textContent = availableCredits;
    });
    this.creditsCalloutTarget.classList.remove(
      'credits-callout--no-credit',
      'credits-callout--low-credit'
    );
    if (availableCredits < this.creditsCalloutTarget.dataset.noCredits) {
      this.creditsCalloutTarget.classList.add('credits-callout--no-credit');
    } else if (availableCredits < this.creditsCalloutTarget.dataset.lowCredits) {
      this.creditsCalloutTarget.classList.add('credits-callout--low-credit');
    }
  }

  // Each section's title sits in the dialog's header, outside this controller, so look in the whole dialog.
  #showSection(sectionClass) {
    this.#hideFlash();
    const dialog = this.element.closest('dialog');
    dialog.querySelectorAll('.enhanced-images__section').forEach((el) => {
      el.classList.add('hide');
    });
    dialog.querySelectorAll(`.${sectionClass}`).forEach((el) => {
      el.classList.remove('hide');
    });

    this.hintTarget.dispatchEvent(new CustomEvent('flash:show'));
  }

  #showFlash(message, level) {
    this.flashTarget.dispatchEvent(new CustomEvent('flash:show', { detail: { message, level } }));
  }

  #hideFlash() {
    this.flashTarget.dispatchEvent(new CustomEvent('flash:hide'));
  }
}
